import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import { breakpoints, layoutPadding } from "../../theme/mediaQueries"
import { colors } from "../../theme/colors"

export const Root = styled.div`
  width: 100%;
  background: url("/images/vp-banner-bg.webp") center / cover no-repeat;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px ${layoutPadding.mobile}px;
  /* EGDE-265: Figma mobile banner gap (content block to CTA) is 32px. */
  gap: 32px;

  /* EGDE-318: the orange band scrolls in EMPTY — its content forms with the
     scroll. Designer r2 (Raz, 2026-07-14): the prototype's entrances are
     HORIZONTAL, not a fade/rise — the copy sweeps in from the inline-start
     screen edge (right in RTL) and the CTA converges from the opposite edge,
     both scrubbed via --scrub so scrolling back reverses them. --scrub
     defaults to 1 (fully shown) so the copy is never lost if JS doesn't run.
     --edge flips the travel for LTR locales — transforms are NOT auto-
     mirrored by stylis-plugin-rtl, so the sign must come from the dir attr. */
  --edge: 1;
  overflow-x: clip;

  [dir="ltr"] & {
    --edge: -1;
  }

  & > * {
    opacity: var(--scrub, 1);
    /* EGDE-479: the still-invisible CTA must not be clickable — the scrub
       callback flips this to auto once the copy is at least half formed.
       Defaults to auto so nothing is lost without JS / reduced motion. */
    pointer-events: var(--content-clicks, auto);
  }

  /* EGDE-510 point 6 (Raz, 2026-07-29: "Animation is missing for mobile").
     The horizontal sweep below used to be unscoped, so mobile got it too. The
     spec splits this component by breakpoint — desktop "Texts enter from right
     to left, button from left to right", but MOBILE "entry from bottom to top".
     A 375px viewport has no room for a 25vw horizontal sweep anyway (it
     measured 94px, which reads as a twitch), so mobile now rises instead. */
  & > div,
  & > a {
    transform: translateY(calc(40px * (1 - var(--scrub, 1))));
  }

  @media (min-width: ${breakpoints.lg}px) {
    /* Copy block (ContentSection) — from the inline-start edge. */
    & > div {
      transform: translateX(
        calc(var(--edge) * min(25vw, 480px) * (1 - var(--scrub, 1)))
      );
    }

    /* CTA — converges from the opposite (inline-end) edge, cf. prototype
       frame f020 where both are clipped at their own screen edges. */
    & > a {
      transform: translateX(
        calc(var(--edge) * min(25vw, 480px) * (var(--scrub, 1) - 1))
      );
    }
  }

  @media (prefers-reduced-motion: reduce) {
    /* Element selectors, not just the universal one — the travel rules above
       are more specific than the child-universal rule and would win otherwise. */
    & > *,
    & > div,
    & > a {
      opacity: 1;
      transform: none;
      pointer-events: auto;
    }
  }

  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: row;
    align-items: center;
    /* EGDE-20: text group anchored to the inline-start (right in RTL) and the
       CTA pushed to the inline-end (left in RTL, flush to the grid padding) —
       matches Figma, where the button sits at the far edge rather than hugging
       the text. */
    justify-content: space-between;
    padding: 64px ${layoutPadding.desktop}px;
    gap: 32px;
  }

  @media (min-width: ${breakpoints.xl}px) {
    padding: 64px ${layoutPadding.desktopL}px;
  }

  @media (min-width: ${breakpoints.xxl}px) {
    padding: 64px ${layoutPadding.desktopXl}px;
  }
`

export const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* EGDE-265: gap between the title group and the countdown. The offer→timer
     gap is larger than the supertitle→offer gap (which stays 8px in TitleGroup).
     Mobile = 16px (Figma mobile separates the offer line from the timer);
     desktop = 11px (annotated Figma spacing between the headline and timer). */
  gap: 16px;

  @media (min-width: ${breakpoints.lg}px) {
    align-items: flex-start;
    gap: 11px;
  }
`

/* EGDE-265: supertitle + headline stay 8px apart at every breakpoint (Figma). */
export const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    align-items: flex-start;
  }
`

export const Supertitle = styled(Typography)`
  font-size: 32px !important;
  font-weight: 700 !important;
  line-height: 38px;
  text-align: center;
  color: #000;

  @media (min-width: ${breakpoints.lg}px) {
    font-weight: 500 !important;
    line-height: 40px;
    text-align: start;
  }
`

export const Headline = styled(Typography)`
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  text-align: center;
  color: #000;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 48px;
    line-height: 48px;
    text-align: start;
  }
`

/* EGDE-264: on mobile the percent was 48px — far larger than the 18px headline
   body, so the "10%" ballooned out of proportion. Figma's mobile banner sets
   the percent to 32px/38px Bold (≈H2 mobile), modestly larger than the 18px
   body. Desktop stays 56px against the 48px headline. */
export const PercentHighlight = styled.span`
  font-weight: 700;
  font-size: 32px;
  line-height: 38px;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 56px;
    line-height: 64px;
  }
`

export const TimerText = styled(Typography)`
  font-size: 32px !important;
  font-weight: 700 !important;
  line-height: 38px;
  text-align: center;
  color: #000;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 56px !important;
    line-height: 64px;
    text-align: start;
  }
`

export const CTAButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 200px;
  min-height: 46px;
  background: #fff;
  border-radius: 9999px;
  padding: 12px 24px;
  cursor: pointer;
  text-decoration: none;
  /* EGDE-503: hover keeps the pill solid white and adds the design-system
     button shadow — fading the white pill over the orange band read as a
     gray wash. */
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: ${colors.shadows.button};
  }

  /* EGDE-500: the white variant presses to the orange ring (inset so the
     1px border never nudges the layout). */
  &:active {
    box-shadow:
      ${colors.shadows.button},
      inset 0 0 0 1px ${colors.primitives.primary500};
  }

  @media (min-width: ${breakpoints.lg}px) {
    min-height: 52px;
  }
`

export const CTAText = styled(Typography)`
  font-size: 18px;
  font-weight: 700 !important;
  line-height: 22px;
  color: #000;
  text-align: center;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 20px;
    line-height: 28px;
  }
`
